import {
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
  Button,
  Div,
  H5,
  Icon,
  Modal,
  PlusIcon,
  PropsModal,
  TextWithTooltipIcon,
  lightTheme,
} from '@vgrubs/components';
import {
  CategoryV2Response,
  MenuItemV2Response,
} from 'components/src/types/menu.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ImageInput } from '../../components/ImageInput';
import { DeleteImageModal } from './DeleteImageModal';
import { AddModifierGroupComponent } from './ModifierGroupComponents';

export const AddItemModal = ({
  addModal,
  closeModal,
  edit,
  setAddModifierGroupModal,
  menu,
  modifierGroups,
  setModifierGroups,
  handleAddItem,
  handleEditItem,
  handleUploadImage,
}: PropsModal & {
  setAddModifierGroupModal: Dispatch<SetStateAction<boolean>>;
  addModal:
    | boolean
    | { item: BTMenuItemResponse; category: BTCategoryResponse }
    | null;
  menu: BTMenuResponse | null;
  handleAddItem?: (
    image: BTMenuItemResponse['imageUrl'] | File | Blob | null,
    name: MenuItemV2Response['name'],
    description: BTMenuItemResponse['description'],
    fromPrice: BTMenuItemResponse['fromPrice'],
    toPrice: BTMenuItemResponse['toPrice'],
    categoryId: BTCategoryResponse['id'],
    modifierGroups: BTMenuItemResponse['modifierGroups'],
    emptyForm: () => void
  ) => void;
  handleEditItem?: (
    image: BTMenuItemResponse['imageUrl'] | File | Blob | null,
    name: MenuItemV2Response['name'],
    description: BTMenuItemResponse['description'],
    fromPrice: BTMenuItemResponse['fromPrice'],
    toPrice: BTMenuItemResponse['toPrice'],
    categoryId: BTCategoryResponse['id'],
    modifierGroups: BTMenuItemResponse['modifierGroups'],
    existingItem: BTMenuItemResponse,
    existingCategoryId: BTCategoryResponse['id'],
    emptyForm: () => void
  ) => void;
  edit?: boolean;
  modifierGroups: BTMenuItemResponse['modifierGroups'];
  setModifierGroups: Dispatch<
    SetStateAction<BTMenuItemResponse['modifierGroups']>
  >;
  handleUploadImage: (
    image: File | Blob | string | null
  ) => Promise<string | undefined>;
}) => {
  const [image, setImage] = useState<string | File | Blob | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [category, setCategory] = useState<CategoryV2Response['id']>(0);
  const [fromPrice, setFromPrice] = useState(0);
  const [toPrice, setToPrice] = useState(0);

  const [deleteImageModal, setDeleteImageModal] = useState(false);

  const data = addModal;

  const handleValueTotalChangeFromPrice = useCallback((val: number) => {
    setFromPrice(val);
  }, []);

  const handleValueTotalChangeToPrice = useCallback((val: number) => {
    setToPrice(val);
  }, []);

  const typeOptions = (
    <optgroup label="Select Category">
      {menu?.categories?.map((r, i) => (
        <option value={r.id} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );


  useEffect(() => {
    setCategory(menu?.categories?.[0]?.id || 0);
    if (edit && typeof data === 'object') {
      setName(data?.item?.name || '');
      setDescription(data?.item?.description || '');
      setCategory(data?.category?.id || menu?.categories?.[0]?.id || 0);
      setFromPrice(data?.item?.fromPrice || 0);
      setToPrice(data?.item?.toPrice || 0);
      setModifierGroups(data?.item?.modifierGroups || []);
      setImage(data?.item?.imageUrl || '');
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setImage('');
    setName('');
    setDescription('');
    setCategory(menu?.categories?.[0]?.id || 0);
    setModifierGroups([]);
    setFromPrice(0);
    setToPrice(0);
  };

  const handleSubmit = () => {
    if (edit && typeof data === 'object') {
      handleEditItem?.(
        image,
        name,
        description,
        fromPrice,
        toPrice,
        category,
        modifierGroups,
        data?.item as BTMenuItemResponse,
        data?.category?.id as BTCategoryResponse['id'],
        emptyForm
      );
    } else {
      handleAddItem?.(
        image,
        name,
        description,
        fromPrice,
        toPrice,
        category,
        modifierGroups,
        emptyForm
      );
    }
  };

  const handleImageChange = async (image: File | Blob | string | null) => {
    const imageUrl = await handleUploadImage(image);
    if (imageUrl) {
      setImage(imageUrl);
    }
  };


  return (
    <>
      <Modal
        isOpen={!!addModal}
        closeModal={closeModal}
        header={edit ? 'Edit Item' : 'Add Item'}
        logo={false}
        footer={
          <Button $selected $w="100%" onClick={handleSubmit}>
            Confirm
          </Button>
        }
        $numOfFloatingButtons={1}
      >
        <Div $flex $center $mb="32">
          <ImageInput
            image={image}
            onChange={(img) => handleImageChange(img)}
            onDelete={() => setDeleteImageModal(true)}
          />
        </Div>
        <Div $flex $column $gap="24px">
          <AdminInput
            placeholder="Enter here"
            label="Item Name"
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
          />

          <AdminSelect
            value={category}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setCategory(Number(target.value));
            }}
            // placeholder="Select location"
            label="Choose Category"
          />
          <Div $flex $column $gap="4px" $alignCenter>
            <TextWithTooltipIcon
              bold
              text="Suggested Price From - To"
              tooltip="The lower the price the higher the chances of people ordering."
            ></TextWithTooltipIcon>

            <Div $flex $alignCenter $gap="16px" $width="100%">
              <AdminDollarInput2
                $textCenter
                onValueChange={handleValueTotalChangeFromPrice}
                value={fromPrice}
              />
              <AdminDollarInput2
                $textCenter
                onValueChange={handleValueTotalChangeToPrice}
                value={toPrice}
              />
            </Div>
          </Div>

          <AdminInput
            placeholder="Enter here"
            label="Description"
            type="textarea"
            value={description}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setDescription(target.value)
            }
          />

          {modifierGroups?.length > 0 ? (
            <>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Modifier Groups</H5>
                <Icon $pointer onClick={() => setAddModifierGroupModal(true)}>
                  <PlusIcon />
                </Icon>
              </Div>
              <div>
                {modifierGroups?.map((item, i) => (
                  <AddModifierGroupComponent
                    item={item}
                    key={i}
                    edit
                    modifierGroups={modifierGroups}
                    setModifierGroups={setModifierGroups}
                  />
                ))}
              </div>
            </>
          ) : (
            <Div
              $flex
              $alignCenter
              $gap="8px"
              $pointer
              onClick={() => setAddModifierGroupModal(true)}
            >
              <Icon>
                <PlusIcon />
              </Icon>

              <H5 $m="0" $color={lightTheme.primary}>
                Add Modifier Groups
              </H5>
            </Div>
          )}
        </Div>
      </Modal>

      <DeleteImageModal
        deleteModal={deleteImageModal}
        closeModal={() => setDeleteImageModal(false)}
        setImage={setImage}
      />
    </>
  );
};
